import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { Popup, Input, Toggle } from '@skytech/ui'
import { useDebounce } from '@skytech/utils'
import FormatListChecksIcon from 'mdi-react/FormatListChecksIcon'
import PackageVariantIcon from 'mdi-react/PackageVariantIcon'
import SortNumericAscendingIcon from 'mdi-react/SortNumericAscendingIcon'
import SortNumericDescendingIcon from 'mdi-react/SortNumericDescendingIcon'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsAdmin } from '@/hooks/access'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import { useShopNewOrderContext } from '@/modules/ecom-policy/shop/new-order/ShopNewOrder.context'
import type { CategoryData } from '@/modules/ecom-policy/types'
import handleMultipleChoiceItemSelect from '@/util/handleMultipleChoiceItemSelect'

import Categories from './Categories'

import './filter.scss'

// todo: fix bug of the search input after choosing a product and clicking out of the input

const Filter: FC<{ availableCategories: CategoryData[] }> = ({ availableCategories }) => {
  const translate = useTranslation()
  const isAdmin = useIsAdmin()
  const shopCtx = useShopNewOrderContext()

  const [querySearch, setQuerySearch] = useSearchParamsByKey('search')
  const searchFilter = shopCtx.searchFilter
  const setSearchFilter = shopCtx.setSearchFilter
  const debouncedSearchTerm = useDebounce(searchFilter, 500)

  const [selectedCategoryIds, selectCategoryIds] = shopCtx.selectedCategoryIds
  const [stockFilter, setStockFilter] = shopCtx.stockFilter
  const [, setSelectedProductIds] = shopCtx.selectedProductIds
  const [sortParam, setSortParam] = shopCtx.sortParam
  const [sortOrder, setSortOrder] = shopCtx.sortOrder
  const [isWholeVendorsCatalogueVisible, setIsWholeVendorsCatalogueVisible] =
    shopCtx.isWholeVendorsCatalogueVisible

  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false)

  const toggleSorting = () => {
    if (!sortParam) {
      setSortParam('priceFrom')
      return
    }

    if (sortOrder === 'asc') {
      setSortOrder('desc')
      return
    }

    setSortOrder('asc')
    setSortParam(undefined)
  }

  const isCategoryFilterActive = Boolean(selectedCategoryIds?.length)

  function handleToggleProductListType() {
    setIsWholeVendorsCatalogueVisible(!isWholeVendorsCatalogueVisible)
    if (isWholeVendorsCatalogueVisible) {
      setSelectedProductIds(null)
    }
  }

  useEffect(() => {
    setSearchFilter(querySearch || '')
  }, [querySearch])

  useEffect(() => {
    setQuerySearch(debouncedSearchTerm)
  }, [debouncedSearchTerm])

  return (
    <div className="products__filter">
      <Input
        id="search-product"
        type="text"
        value={searchFilter}
        onChange={e => setSearchFilter(e.target.value)}
        placeholder={translate('Search')}
      />

      <div className="input-container sorting" title={translate('Sort by price')}>
        <button className={sortParam === 'priceFrom' ? 'selected' : ''} onClick={toggleSorting}>
          {sortOrder === 'asc' ? <SortNumericAscendingIcon /> : <SortNumericDescendingIcon />}
        </button>
      </div>
      <div className="input-container stock" title={translate('Show only products in stock')}>
        <button
          className={stockFilter ? 'selected' : ''}
          onClick={() => setStockFilter(!stockFilter)}
        >
          <PackageVariantIcon />
        </button>
      </div>

      {Boolean(availableCategories.length) && (
        <div className="input-container categories" title={translate('Apply category filter')}>
          <button
            className={isCategoryFilterActive ? 'selected' : ''}
            onClick={() => setIsCategoriesModalOpen(true)}
          >
            <FormatListChecksIcon />
          </button>
        </div>
      )}

      {isAdmin && (
        <div className="ml-3" title={translate("Show vendor's whole catalogue")}>
          <Toggle
            id="show-vendors-whole-catalogue"
            checked={isWholeVendorsCatalogueVisible}
            label={<Translation id="Show vendor's whole catalogue" />}
            onChange={handleToggleProductListType}
          />
        </div>
      )}

      <Popup
        isOpen={isCategoriesModalOpen}
        onClose={() => setIsCategoriesModalOpen(false)}
        ariaLabel={translate('Category filters')}
      >
        <Categories
          selectedCategoryIds={selectedCategoryIds}
          availableCategories={availableCategories}
          onCategorySelect={(selectedId: number) =>
            handleMultipleChoiceItemSelect({
              selectedId,
              currentIdList: selectedCategoryIds,
              setData: selectCategoryIds,
            })
          }
        />
      </Popup>
    </div>
  )
}

export default Filter
